import { useCallback, useRef } from "react";

import { getMapPinsWithinBounds } from "@/api/places";

import {
  MAP_PIN_CACHE_TTL_MS,
  MAP_PIN_SEARCH_LIMIT,
} from "./constants";
import {
  getMapPinBoundsKey,
  normalizeMapPinBounds,
  pruneMapPinCache,
} from "./map-pin-bounds";

export const useMapPinRefresh = ({ generateMapMarkers, setMarkers }) => {
  const mapPinCacheRef = useRef(new Map());
  const mapPinRequestIdRef = useRef(0);
  const mapPinResultsVisibleRef = useRef(false);

  const resetMapPinResultsVisible = useCallback(() => {
    mapPinResultsVisibleRef.current = false;
  }, []);

  const refreshMapMarkersForBounds = useCallback(
    async (bounds) => {
      const normalizedBounds = normalizeMapPinBounds(bounds);
      if (!normalizedBounds) return;

      const cacheKey = getMapPinBoundsKey(normalizedBounds);
      const cachedResult = mapPinCacheRef.current.get(cacheKey);
      const now = Date.now();

      if (cachedResult && now - cachedResult.cachedAt < MAP_PIN_CACHE_TTL_MS) {
        mapPinResultsVisibleRef.current = true;
        setMarkers(
          generateMapMarkers(cachedResult.places, { syncSearchCenter: false })
        );
        return;
      }

      const requestId = mapPinRequestIdRef.current + 1;
      mapPinRequestIdRef.current = requestId;

      try {
        const response = await getMapPinsWithinBounds({
          ...normalizedBounds,
          limit: MAP_PIN_SEARCH_LIMIT,
          langDivCd: "KOR",
        });
        if (requestId !== mapPinRequestIdRef.current) return;

        const nextPlaces = Array.isArray(response) ? response : [];
        mapPinCacheRef.current.set(cacheKey, {
          cachedAt: now,
          places: nextPlaces,
        });
        pruneMapPinCache(mapPinCacheRef.current);
        mapPinResultsVisibleRef.current = true;
        setMarkers(generateMapMarkers(nextPlaces, { syncSearchCenter: false }));
      } catch (error) {
        console.warn("Map pin refresh failed:", error);
      }
    },
    [generateMapMarkers, setMarkers]
  );

  return {
    mapPinResultsVisibleRef,
    refreshMapMarkersForBounds,
    resetMapPinResultsVisible,
  };
};
